/** @jsxRuntime classic */
/** @jsx jsx */
import type { FC } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import SpacesIcon from '@atlaskit/icon-lab/core/spaces';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { token } from '@atlaskit/tokens';

import { SessionLastUpdated } from '@atlassian/rovo-platform-ui-components/session-details/SessionLastUpdated';

import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';
import { LivePagePreloader } from '@confluence/live-page-preloader/entry-points/LivePagePreloader.ts';
import { CONTEXT_PATH } from '@confluence/named-routes/entry-points/context.ts';
import { SPACE_OVERVIEW } from '@confluence/named-routes/entry-points/routes.ts';

import {
	RecentWorkListItemLayout,
	type RecentWorkListItemLayoutProps,
} from '../layouts/RecentWorkListItemLayout.tsx';
import { recentWorkMessages } from '../recentWorkMessages.ts';

import { getTitle } from './EmojiContentIcon.tsx';
import { StatusLozenge } from './StatusLozenge.tsx';
import {
	AgentSessionListItemView as DedicatedAgentSessionListItemView,
	type AgentSessionListItemViewProps,
} from './agent-sessions/AgentSessionListItemView.tsx';
import { getContentIcon, getContentTypeMessage } from './recentWorkContent.tsx';

const styles = cssMap({
	titleAndStatus: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	title: {
		// Sized to the title text (shrinking with ellipsis when long) so the status lozenge sits
		// right after the title rather than at the far end of the row.
		maxWidth: '100%',
		minWidth: 0,
		position: 'relative',
		zIndex: 1,
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'none',
		},
	},
	titleText: {
		display: 'block',
		overflow: 'hidden',
		color: token('color.text'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		width: '100%',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	metadata: {
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		whiteSpace: 'nowrap',
	},
	separator: {
		flexShrink: 0,
		marginInlineStart: token('space.050'),
		marginInlineEnd: token('space.050'),
	},
	spaceLinkWrapper: {
		position: 'relative',
		zIndex: 1,
		display: 'flex',
		alignItems: 'center',
		overflow: 'hidden',
		minWidth: 0,
		columnGap: token('space.050'),
	},
	spaceLink: {
		overflow: 'hidden',
		color: token('color.text.subtlest'),
		textDecoration: 'none',
		textOverflow: 'ellipsis',
		'&:visited': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
		'&:hover': {
			color: token('color.text.subtlest'),
			textDecoration: 'none',
		},
	},
});

type RecentWorkContent = {
	id: string;
	type?: string;
	subType?: string | null;
	status?: string;
	title?: { text?: string; emoji?: string };
	links?: { webui?: string };
	space?: { name?: string; spaceKey?: string };
	lastAction?: { when?: string };
};

type RecentWorkListItemViewProps = Pick<RecentWorkListItemLayoutProps, 'onClick'> & {
	content: RecentWorkContent;
	onSpaceClick?: () => void;
	action?: string;
	lastActionI18n?: MessageDescriptor;
};

export const RecentWorkListItemView: FC<RecentWorkListItemViewProps> = ({
	content,
	onClick,
	onSpaceClick,
	action,
	lastActionI18n,
}) => {
	const { formatMessage } = useIntl();
	const isLivePage =
		content.type === 'page' && content.subType === 'live' && content.status !== 'archived';
	const url = `${CONTEXT_PATH}${content.links?.webui}`;
	const title = getTitle(content.title?.text, Boolean(content.title?.emoji));
	const titleContent = title || formatMessage(recentWorkMessages.contentUntitled);
	const contentTypeMessage = getContentTypeMessage(content.type, content.subType);
	const space = content.space;

	const primary = (
		<div css={styles.titleAndStatus}>
			<Tooltip content={titleContent} position="bottom">
				{(tooltipProps) => (
					<Anchor
						xcss={styles.title}
						href={url}
						tabIndex={-1}
						aria-hidden
						testId="recent-card-title"
						{...tooltipProps}
						onClick={(event) => {
							tooltipProps.onClick(event);
							onClick?.(event);
						}}
					>
						<span css={styles.titleText}>{titleContent}</span>
					</Anchor>
				)}
			</Tooltip>
			<StatusLozenge status={content.status || ''} />
		</div>
	);

	const secondary = (
		<div css={styles.metadata}>
			{contentTypeMessage && <FormattedMessage {...contentTypeMessage} />}
			{contentTypeMessage && space?.name && (
				<Box as="span" xcss={styles.separator} aria-hidden="true">
					·
				</Box>
			)}
			{space?.name && (
				<Box xcss={styles.spaceLinkWrapper}>
					<SpacesIcon
						label=""
						size="small"
						color={token('color.icon.subtle')}
						testId="recent-work-space-icon"
					/>
					{space.spaceKey ? (
						<Anchor
							xcss={styles.spaceLink}
							href={SPACE_OVERVIEW.toUrl({ spaceKey: space.spaceKey })}
							onClick={onSpaceClick}
						>
							{space.name}
						</Anchor>
					) : (
						space.name
					)}
				</Box>
			)}
		</div>
	);

	const icon = getContentIcon(content.type, content.subType, content.title?.emoji);
	const timeAgo = content.lastAction?.when ? (
		<SessionLastUpdated lastStateChangeAt={new Date(content.lastAction.when).getTime()} />
	) : undefined;

	const card = (
		<RecentWorkListItemLayout
			url={url}
			ariaLabel={titleContent}
			onClick={onClick}
			testId="recent-work-list-item"
			icon={icon}
			secondary={secondary}
			lastModified={
				timeAgo && lastActionI18n ? (
					<FormattedMessage {...lastActionI18n} values={{ timeAgo }} />
				) : (
					timeAgo
				)
			}
			actions={action}
		>
			{primary}
		</RecentWorkListItemLayout>
	);

	return (
		<LivePagePreloader isLivePage={isLivePage}>
			<HoverPreloader
				name="recent-work-list-item"
				preloadUrl={url}
				preloadOptions={PreloadingPresets.onlyJS}
			>
				{card}
			</HoverPreloader>
		</LivePagePreloader>
	);
};

export const AgentSessionListItemView: FC<AgentSessionListItemViewProps> =
	DedicatedAgentSessionListItemView;
